import { type FC, type RefObject, useCallback, useRef } from 'react'

import { type ReactFlowState, useReactFlow, useStore } from '@xyflow/react'
import { useAtomValue, useSetAtom } from 'jotai'
import { AnimatePresence, motion } from 'framer-motion'
import {
  BorderRadius8,
  GreyGrey30,
  GreyWhite,
  ShadowL,
  Flex
} from '@sequencehq/core-components'
import {
  addNodeAtom,
  showAddNodeToolbarAtom,
  useResolvedNodeDefinitions
} from 'Automations/automationEditor/atoms'
import { NodeType } from 'Automations/nodeDefinitions/types'
import { useAddNodeItems } from '../../hooks'
import { NodeIconButton } from '../ReactFlow/NodeIconButton'
import { useDragWithMorph } from './useDragWithMorph'

const zoomSelector = (state: ReactFlowState) => state.transform[2]

export const AddNodeToolbar: FC = () => {
  const showAddNodeToolbar = useAtomValue(showAddNodeToolbarAtom)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const items = useAddNodeItems()

  return (
    <AnimatePresence initial={false}>
      {showAddNodeToolbar && (
        <motion.div
          style={{
            position: 'absolute',
            left: '50%',
            x: '-50%',
            bottom: '12px'
          }}
          initial={{ y: '200%' }}
          animate={{ y: '0px' }}
          exit={{ y: '200%' }}
          transition={{ duration: 0.2 }}
        >
          <Flex
            ref={toolbarRef}
            data-testid="automations.editor.addNodeToolbar"
            backgroundColor={GreyWhite}
            border={`${GreyGrey30} 1px solid`}
            borderRadius={BorderRadius8}
            boxShadow={ShadowL}
            padding="8px"
            gap="8px"
          >
            {items.map(item => (
              <ButtonAddNode
                key={item.nodeType}
                nodeType={item.nodeType}
                config={item.config}
                toolbarRef={toolbarRef}
              />
            ))}
          </Flex>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const ButtonAddNode = <Config extends Record<string, unknown>>({
  nodeType,
  config,
  toolbarRef
}: {
  nodeType: NodeType
  config?: Partial<Config>
  toolbarRef: RefObject<HTMLDivElement | null>
}) => {
  const addNode = useSetAtom(addNodeAtom)
  const { screenToFlowPosition } = useReactFlow()
  const zoom = useStore(zoomSelector)
  const Icon = useResolvedNodeDefinitions()[nodeType].icon

  const onClick = useCallback(() => {
    const canvasEl = document.querySelector(
      '[data-testid="automations.editor.canvas"]'
    )
    if (!canvasEl) {
      addNode({ nodeType, config })
      return
    }
    const rect = canvasEl.getBoundingClientRect()
    const center = screenToFlowPosition({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    })
    addNode({
      nodeType,
      config,
      position: {
        x: center.x + Math.random() * 150 - 75,
        y: center.y + Math.random() * 150 - 75
      }
    })
  }, [addNode, nodeType, config, screenToFlowPosition])

  const renderIcon = useCallback(() => <Icon boxSize="32px" />, [Icon])

  const { onDragStart, onDragEnd } = useDragWithMorph({
    toolbarRef,
    renderIcon,
    nodeType,
    zoom
  })

  const handleDragStart = useCallback(
    (event: React.DragEvent) => {
      event.dataTransfer.setData(
        'application/automations-node',
        JSON.stringify({ nodeType, config, offsetX: -16, offsetY: -16 })
      )
      event.dataTransfer.effectAllowed = 'move'
      onDragStart(event)
    },
    [nodeType, config, onDragStart]
  )

  return (
    <NodeIconButton
      nodeType={nodeType}
      data-testid={`automations.editor.addNode.${nodeType}`}
      onClick={onClick}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
    />
  )
}
